import React, { useEffect, useMemo } from "react"

import { useSearchParams } from "react-router-dom"

import Icon from "@/components/Icon"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/contexts/AuthContext"

import RoleTeamGuard from "../components/guards/RoleTeamGuard"
import PeopleContent from "../components/people/PeopleContent"
import ComplianceOverviewTab from "../components/people/tabs/ComplianceOverviewTab"
import DiaryEntriesOverviewTab from "../components/people/tabs/DiaryEntriesOverviewTab"
import { GeneralOverview } from "../components/people/tabs/GeneralOverview"
import DashboardLayout from "../layouts/DashboardLayout"

const People: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { claims } = useAuth()
  const personId = searchParams.get("personId")
  const tabParam = searchParams.get("tab")

  const isAdmin = claims.user_role === "Admin"
  const isManager = claims.user_role === "Manager"
  const canAccessOverview = isAdmin || isManager

  // Determine default tab - if tab is specified in URL, use it; otherwise default to "people"
  // If user doesn't have access to overview/diary tabs, redirect to "people"
  // Support both "outstanding-diary" (old) and "diary-entries" (new) for backward compatibility
  const defaultTab = useMemo(() => {
    const diaryTab = tabParam === "outstanding-diary" ? "diary-entries" : tabParam
    if (
      !canAccessOverview &&
      (tabParam === "people-overview" ||
        tabParam === "outstanding-diary" ||
        tabParam === "diary-entries" ||
        tabParam === "compliance")
    ) {
      return "people"
    }
    if (
      tabParam === "people" ||
      tabParam === "people-overview" ||
      tabParam === "outstanding-diary" ||
      tabParam === "diary-entries" ||
      tabParam === "compliance"
    ) {
      return diaryTab || tabParam
    }
    return "people"
  }, [tabParam, canAccessOverview])

  const [activeTab, setActiveTab] = React.useState(defaultTab)

  // Update active tab when URL params change
  useEffect(() => {
    // Support both "outstanding-diary" (old) and "diary-entries" (new) for backward compatibility
    const normalizedTab = tabParam === "outstanding-diary" ? "diary-entries" : tabParam
    if (
      tabParam &&
      tabParam !== activeTab &&
      (tabParam === "people" ||
        tabParam === "people-overview" ||
        tabParam === "outstanding-diary" ||
        tabParam === "diary-entries" ||
        tabParam === "compliance")
    ) {
      // If user doesn't have access to overview/diary tabs, redirect to "people"
      if (
        !canAccessOverview &&
        (tabParam === "people-overview" ||
          tabParam === "outstanding-diary" ||
          tabParam === "diary-entries" ||
          tabParam === "compliance")
      ) {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set("tab", "people")
        setSearchParams(newSearchParams, { replace: true })
        setActiveTab("people")
        return
      }
      // Normalize old "outstanding-diary" to "diary-entries" in URL
      if (tabParam === "outstanding-diary" && normalizedTab !== activeTab) {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set("tab", "diary-entries")
        setSearchParams(newSearchParams, { replace: true })
        setActiveTab("diary-entries")
        return
      }
      setActiveTab(normalizedTab || tabParam)
    }
  }, [tabParam, activeTab, canAccessOverview, searchParams, setSearchParams])

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    // Prevent users without access from accessing overview/diary tabs
    if (
      !canAccessOverview &&
      (value === "people-overview" || value === "diary-entries" || value === "compliance")
    ) {
      return
    }
    setActiveTab(value)
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set("tab", value)
    // Keep personId if it exists
    setSearchParams(newSearchParams, { replace: true })
  }

  return (
    <DashboardLayout>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full">
        <TabsList className="w-full mb-4">
          <TabsTrigger value="people" className="flex-1">
            <Icon name="user" className="h-4 w-4 mr-2 text-inherit" />
            People
          </TabsTrigger>
          <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
            <TabsTrigger value="people-overview" className="flex-1">
              <Icon name="layout" className="h-4 w-4 mr-2 text-inherit" />
              Overview
            </TabsTrigger>
          </RoleTeamGuard>
          <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
            <TabsTrigger value="diary-entries" className="flex-1">
              <Icon name="notebook" className="h-4 w-4 mr-2 text-inherit" />
              Diary entries
            </TabsTrigger>
          </RoleTeamGuard>
          <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
            <TabsTrigger value="compliance" className="flex-1">
              <Icon name="shieldCheck" className="h-4 w-4 mr-2 text-inherit" />
              Compliance
            </TabsTrigger>
          </RoleTeamGuard>
        </TabsList>
        <TabsContent value="people">
          <PeopleContent personId={personId} />
        </TabsContent>
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <TabsContent value="people-overview">
            <GeneralOverview />
          </TabsContent>
        </RoleTeamGuard>
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <TabsContent value="diary-entries">
            <DiaryEntriesOverviewTab />
          </TabsContent>
        </RoleTeamGuard>
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <TabsContent value="compliance">
            <ComplianceOverviewTab />
          </TabsContent>
        </RoleTeamGuard>
      </Tabs>
    </DashboardLayout>
  )
}

export default People
